// src/components/LegalModal.jsx
import React, { useEffect } from 'react';

/**
 * Modal interactivo para el Aviso de Privacidad Oficial de Xolo Fest 2026.
 * Documento oficial de Entretenimiento Mexicano Deportivo, S. de R.L. de C.V.
 */
export default function LegalModal({ isOpen, onClose }) {
  // Cerrar con tecla Escape y bloquear scroll del body
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop-custom d-flex align-items-center justify-content-center p-3"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 1050
      }}
      onClick={onClose}
    >
      <div
        className="modal-content-card rounded-4 p-4 text-start text-light"
        style={{
          backgroundColor: '#121217',
          border: '1px solid rgba(220, 38, 38, 0.35)',
          maxWidth: '680px',
          width: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7), 0 0 30px rgba(220, 38, 38, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
      >
        {/* Cabecera del Modal con Ícono de Escudo Nítido */}
        <div className="d-flex align-items-center justify-content-between pb-3 mb-3 border-bottom border-secondary border-opacity-25">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center bg-danger rounded-circle shadow-sm"
              style={{ width: '42px', height: '42px', minWidth: '42px', minHeight: '42px' }}
            >
              <i className="bi bi-shield-lock-fill text-white fs-5" aria-hidden="true"></i>
            </div>
            <div>
              <h2 id="legal-modal-title" className="h5 text-white fw-bold mb-0">
                Aviso de Privacidad
              </h2>
              <small className="text-secondary">Xolo Fest 2026 – Club Tijuana Xoloitzcuintles de Caliente</small>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '34px', height: '34px' }}
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <i className="bi bi-x-lg text-white"></i>
          </button>
        </div>

        {/* Cuerpo del Modal con scroll con el contenido exacto del Aviso de Privacidad */}
        <div
          className="modal-body-scroll pe-2"
          style={{
            overflowY: 'auto',
            fontSize: '0.88rem',
            lineHeight: '1.6',
            color: '#d4d4d8'
          }}
        >
          <div className="mb-3 pb-2 border-bottom border-secondary border-opacity-25">
            <span className="text-danger fw-bold">SITIO WEB: </span>
            <span className="text-white fw-bold">WWW.XOLOFEST.MX</span>
          </div>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2">
            IDENTIDAD Y DOMICILIO DEL RESPONSABLE
          </h3>
          <p>
            Entretenimiento Mexicano Deportivo, S. de R.L. de C.V., (&quot;en adelante EL CLUB&quot;) con domicilio en Boulevard Agua Caliente No. 12027, Hipódromo, C.P. 22020, Tijuana, Baja California, tiene la convicción de proteger la información personal proporcionada por sus usuarios (en adelante “DATOS PERSONALES”) y es el responsable de su tratamiento cuando sean recabados a través del sitio de Internet del Club Tijuana Xoloitzcuintles de Caliente, aplicación Oficial del Club Tijuana Xoloitzcuintles de Caliente (en adelante el “Sitio”), medios impresos, correo electrónico y/o vía telefónica (en adelante “Otros Medios”).
          </p>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2 mt-4">
            DATOS PERSONALES QUE SERÁN TRATADOS POR EL RESPONSABLE
          </h3>
          <p>
            A. Datos personales obtenidos directamente de usted. La información que recopilamos acerca de usted serán datos personales que nos ha proporcionado directamente, a través de nuestros sitios web, tiendas virtuales, aplicaciones móviles, plataforma de WhatsApp, en nuestras tiendas físicas, etc. Los datos personales tratados son los que a continuación se indican:
          </p>

          <div className="ps-2 mb-2">
            <strong className="text-white d-block mb-1">I. DATOS PERSONALES:</strong>
            <ul className="ps-3 mb-2" style={{ listStyleType: 'lower-roman' }}>
              <li>Nombre, domicilio, correo electrónico, RFC.</li>
              <li>Número telefónico, fijo o celular.</li>
              <li>Edad.</li>
              <li>Fecha de nacimiento.</li>
              <li>Talla de ropa y/o calzado.</li>
            </ul>
          </div>

          <div className="ps-2 mb-2">
            <strong className="text-white d-block mb-1">II. DATOS FINANCIEROS:</strong>
            <ul className="ps-3 mb-2" style={{ listStyleType: 'lower-roman' }}>
              <li>Información de pago (Número de tarjeta, su nombre, fecha de vencimiento y código de seguridad).</li>
              <li>Clave bancaria estandarizada (CLABE).</li>
              <li>Dirección de facturación.</li>
            </ul>
          </div>

          <div className="ps-2 mb-2">
            <strong className="text-white d-block mb-1">III. DATOS PERSONALES SENSIBLES:</strong>
            <ul className="ps-3 mb-2" style={{ listStyleType: 'lower-roman' }}>
              <li>Se hace de su conocimiento que, EL CLUB no trata datos sensibles a través de su página web, Plataforma de WhatsApp, tiendas físicas, ni la App señalada anteriormente.</li>
            </ul>
          </div>

          <div className="ps-2 mb-3">
            <strong className="text-white d-block mb-1">IV. DATOS QUE RECOPILAMOS DE MANERA AUTOMÁTICA:</strong>
            <ul className="ps-3 mb-2" style={{ listStyleType: 'lower-roman' }}>
              <li>Cookies y dispositivos similares: utilizamos diversas tecnologías para recoger y almacenar información cuando visita nuestros sitios web o utiliza nuestras aplicaciones, y esto incluirá el envío de una o más cookies u otros dispositivos similares en el dispositivo.</li>
            </ul>
          </div>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2 mt-4">
            FINALIDADES DEL TRATAMIENTO DE DATOS
          </h3>
          <ol className="ps-3 mb-3">
            <li className="mb-2">Contratar con EL CLUB la compraventa de mercancía ofrecida por EL CLUB vía Internet y a través de aplicaciones y/o Plataforma de WhatsApp.</li>
            <li className="mb-2">Poder brindarle los servicios contratados de manera eficiente y con la calidad debida.</li>
            <li className="mb-2">proporcionar información en relación con el equipo de fútbol propiedad de EL CLUB, como fechas de partidos, venta de boletos, programa de lealtad de aficionados, eventos especiales del equipo, concursos y trivias; y generar bases de datos que se podrán utilizar con fines de publicidad y promoción relacionadas con lo anterior.</li>
          </ol>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2 mt-4">
            MEDIOS PARA EJERCER LOS DERECHOS DE ACCESO, RECTIFICACIÓN, CANCELACIÓN U OPOSICIÓN (&quot;DERECHOS ARCO&quot;)
          </h3>
          <p>
            De conformidad con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (en adelante &quot;LA LEY DE DATOS&quot;), usted tiene el derecho de acceder a los datos que posee EL CLUB y a los detalles del tratamiento de estos, así como a rectificarlos en caso de ser inexactos o incompletos; cancelarlos en los términos de LA LEY DE DATOS u oponerse al tratamiento de estos para fines específicos (Derecho ARCO). Para ejercer los Derechos ARCO, usted o su representante legal deberán realizar un escrito libre describiendo el motivo y derecho que desea ejercer y enviarla escaneada y firmada al correo electrónico: <strong className="text-white">paulina.garcia@xolos.com.mx</strong> para su atención y seguimiento. La solicitud deberá incluir la siguiente información:
          </p>
          <ol className="ps-3 mb-3">
            <li className="mb-1">Indicar su nombre completo, domicilio y correo electrónico para poder comunicarle la respuesta a su solicitud.</li>
            <li className="mb-1">Indicar los datos personales respecto de los que se busca ejercer alguno de los derechos.</li>
            <li className="mb-1">Anexar cualquier documento o información que facilite la localización de sus datos personales.</li>
            <li className="mb-1">Anexar copia del documento que acredite su identidad (credencial de elector, cédula profesional o pasaporte vigente).</li>
          </ol>
          <p>
            Si la solicitud se tramita a través de un representante legal, incluir el poder notarial en el que conste la facultad otorgada por Usted para este trámite, o bien, una carta poder otorgada por usted con la firma de aceptación del apoderado, ante la presencia de dos testigos, debiendo incluir el nombre, firma, y copia fotostática de la identificación oficial de cada uno de los firmantes. Para el caso de rectificación de datos, adjuntar la documentación que acredite dicha modificación.
          </p>
          <p>
            EL CLUB dará respuesta a dicha solicitud en un plazo no mayor a 30 días hábiles por el mismo medio por el cual usted haya realizado su solicitud, por medio de copias simples o electrónicamente, según sea el caso. En caso de que su solicitud se conteste de manera afirmativa o procedente, los cambios solicitados se harán en un plazo máximo de 15 días hábiles. En caso de que usted solicite el acceso a sus datos personales, el Responsable le informará mediante el correo electrónico en el que comuniquemos nuestra respuesta a su solicitud, el medio por el cual se le dará acceso a su información en caso de ser procedente. El Responsable podrá ampliar los plazos referidos en este párrafo, por una sola vez, por un periodo igual al original, lo cual le será informado.
          </p>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2 mt-4">
            TRANSFERENCIA DE DATOS PERSONALES
          </h3>
          <p>
            EL CLUB podrá, sin el consentimiento del titular, revelar, divulgar y/o transferir dentro y fuera del país los Datos Personales proporcionados por los usuarios, a sus empresas filiales, subsidiarias y/o relacionadas, así como para dar cumplimiento con disposiciones legales que así lo requieran, o bien sea solicitado por autoridades competentes.
          </p>
          <p>
            De igual forma podrá revelar, divulgar y/o transferir dentro y fuera del país los Datos Personales a proveedores, patrocinadores, publicistas, contratistas y/o socios para fines comerciales, como proporcionar datos estadísticos a anunciantes potenciales, enviar publicidad a los usuarios de acuerdo a sus intereses específicos y conducir investigaciones de mercado. Los terceros que reciban la información relacionada con los Datos Personales de los usuarios están obligados a cumplir con lo previsto en este Aviso de Privacidad, así como con los Términos y Condiciones del Sitio y de la legislación aplicable en México.
          </p>
          <p>
            El titular de los Datos Personales podrá otorgar su consentimiento a la transferencia de sus Datos Personales en los términos descritos en el presente Aviso al momento de registrarse al Sitio.
          </p>

          <h3 className="h6 text-danger fw-bold text-uppercase mb-2 mt-4">
            CAMBIOS AL AVISO DE PRIVACIDAD
          </h3>
          <p>
            EL CLUB se reserva el derecho de efectuar en cualquier momento modificaciones o actualizaciones al presente Aviso de Privacidad, para la atención de reformas legislativas o jurisprudenciales, políticas internas, o nuevos requerimientos, siendo la versión actualizada que haya sido publicada, la aplicable en todo momento. Estas modificaciones estarán disponibles al público a través de nuestra página de Internet www.xolos.com.mx en el apartado Aviso de privacidad, o en su caso se podrán enviar al último correo electrónico o, en su caso, domicilio, que nos haya proporcionado. Para el caso de la App, los cambios al aviso de privacidad se harán mediante notificaciones directamente en la aplicación.
          </p>

          <div className="p-3 my-3 rounded bg-dark border border-secondary border-opacity-50">
            <p className="mb-2">
              Reconozco que he leído y entiendo el alcance y significado del presente aviso a lo cual manifiesto mi consentimiento, así como de los mecanismos que LA LEY DE DATOS, su Reglamento, los Lineamientos y el presente Aviso de Privacidad me confieren para el ejercicio de mis derechos de acceso, rectificación, cancelación y oposición, así como de la limitación del uso, transferencia y divulgación de mis datos que pueda realizar Entretenimiento Mexicano Deportivo, S. de R.L. de C.V.
            </p>
            <p className="mb-0 fw-bold text-white">
              Mediante el uso de nuestra página web, aplicación y plataforma de WhatsApp, Usted da su consentimiento para el tratamiento y transferencias.
            </p>
          </div>
        </div>

        {/* Pie del Modal */}
        <div className="pt-3 mt-3 border-top border-secondary border-opacity-25 text-end">
          <button
            type="button"
            className="btn btn-xolo-primary px-4 py-2 fw-semibold"
            onClick={onClose}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
